import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Grid,
  Group,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tabs,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { useDropzone } from "react-dropzone";
import { IconPhoto } from "@tabler/icons-react";
import { z } from "zod";
import i18n from "src/i18n";

import { useCreateMasterclass } from "@hooks/mutations/masterclass/useCreateMasterclass";
import { useUpdateMasterclass } from "@hooks/mutations/masterclass/useUpdateMasterclass";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useAuth } from "@contexts/auth";
import { ROLES } from "@constants/index";
import { imageFileTypes } from "@constants/fileTypes";
import { Masterclass } from "@customTypes/masterclass";
import { CustomFormBuilder } from "@components/CustomFormBuilder";

const schema = () =>
  z.object({
    name: z.string().min(1, i18n.t("name-required")),
    datetime: z.date({ required_error: i18n.t("date-required") }),
    price: z.number().min(0),
    provider_id: z.string().min(1, i18n.t("provider-required")),
    slug: z.string().min(1, i18n.t("slug-required")),
    description: z.string().optional(),
    limit: z.number().optional(),
    mode: z.enum(["online", "in_person"]),
    location: z.string().optional(),
  });

type Inputs = z.infer<ReturnType<typeof schema>>;

interface MasterclassModalProps {
  opened: boolean;
  close: () => void;
  masterclass: Masterclass | null;
}

export const MasterclassModal = ({ opened, close, masterclass }: MasterclassModalProps) => {
  const { t } = useTranslation();
  const { id: userId, roles } = useAuth();
  const isAdmin = roles.includes(ROLES.ADMIN) || roles.includes(ROLES.RECEPTIONIST);

  const isEdit = !!masterclass;

  const { mutate: createMasterclass, isLoading: isCreating } = useCreateMasterclass();
  const { mutate: updateMasterclass, isLoading: isUpdating } = useUpdateMasterclass(masterclass?._id || "");
  
  const { data: providersData } = useGetUsers({ role: "doctor" });

  const providersOptions = useMemo(() => {
    return (providersData || []).map(p => ({
      label: p.fullName,
      value: p.id,
    }));
  }, [providersData]);

  const [bannerFile, setBannerFile] = useState<File | null>(null);

  const form = useForm<Inputs>({
    validate: zodResolver(schema()),
    initialValues: {
      name: "",
      datetime: new Date(),
      price: 0,
      provider_id: isAdmin ? "" : userId,
      slug: "",
      description: "",
      limit: undefined,
      mode: "online",
      location: "",
    },
  });

  // Populate form for editing
  useEffect(() => {
    if (masterclass && opened) {
      form.setValues({
        name: masterclass.name || "",
        datetime: new Date(masterclass.datetime),
        price: masterclass.price || 0,
        provider_id: masterclass.provider_id || "",
        slug: masterclass.slug || "",
        description: masterclass.description || "",
        limit: masterclass.limit,
        mode: masterclass.mode || "online",
        location: masterclass.location || "",
      });
    } else if (!opened) {
        form.reset();
        setBannerFile(null);
    }
  }, [masterclass, opened]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setBannerFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": imageFileTypes },
    multiple: false,
  });

  const handleFormSubmit = (values: Inputs) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("datetime", values.datetime.toISOString());
    formData.append("price", values.price.toString());
    formData.append("provider_id", values.provider_id);
    formData.append("slug", values.slug);
    if (values.description) {
      formData.append("description", values.description);
    }
    if (values.limit) {
      formData.append("limit", values.limit.toString());
    }
    formData.append("mode", values.mode);
    if (values.location) {
      formData.append("location", values.location);
    }

    if (bannerFile) {
      formData.append("banner_file", bannerFile);
    }

    if (isEdit) {
      updateMasterclass(formData, {
        onSuccess: () => {
          close();
        }
      });
    } else {
      createMasterclass(formData, {
        onSuccess: () => {
          close();
        }
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<Text fw={600}>{isEdit ? t("edit-masterclass") : t("create-masterclass")}</Text>}
      size="lg"
      centered
    >
      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details">{t("details")}</Tabs.Tab>
          <Tabs.Tab value="form" disabled={!isEdit}>{t("registration-form")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details">
          <form onSubmit={form.onSubmit(handleFormSubmit)} style={{ marginTop: '1rem' }}>
            <Stack gap="md">
              <TextInput
            label={t("masterclass-name")}
            placeholder={t("eg", { example: "Maternal Health 101" })}
            withAsterisk
            {...form.getInputProps("name")}
            onChange={(event) => {
              const val = event.currentTarget.value;
              form.setFieldValue("name", val);
              // Auto-generate slug only when creating
              if (!isEdit) {
                form.setFieldValue("slug", val.toLowerCase().replace(/ /g, "-").replace(/[^\w-]/g, ""));
              }
            }}
          />

          <TextInput
            label={t("masterclass-slug")}
            placeholder="maternal-health-101"
            withAsterisk
            disabled={isEdit} // Slugs are usually permanent
            {...form.getInputProps("slug")}
          />

          <Grid>
            <Grid.Col span={6}>
              <DateTimePicker
                label={t("masterclass-datetime")}
                placeholder={t("select-date-time")}
                withAsterisk
                {...form.getInputProps("datetime")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label={t("masterclass-price")}
                placeholder="5000"
                withAsterisk
                min={0}
                {...form.getInputProps("price")}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <Radio.Group
                label={t("masterclass-mode")}
                withAsterisk
                {...form.getInputProps("mode")}
              >
                <Group mt="xs">
                  <Radio value="online" label={t("online")} />
                  <Radio value="in_person" label={t("in-person")} />
                </Group>
              </Radio.Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label={t("participant-limit")}
                placeholder={t("unlimited")}
                min={1}
                {...form.getInputProps("limit")}
              />
            </Grid.Col>
          </Grid>
          
          <TextInput
            label={form.values.mode === 'online' ? t("zoom-link", "Zoom/Meeting Link") : t("physical-location", "Physical Location")}
            placeholder={form.values.mode === 'online' ? "https://zoom.us/j/..." : "123 Health Ave, City"}
            {...form.getInputProps("location")}
          />

          {isAdmin && (
            <Select
              label={t("masterclass-provider")}
              placeholder={t("select-provider")}
              data={providersOptions}
              searchable
              withAsterisk
              {...form.getInputProps("provider_id")}
            />
          )}

          <Textarea
            label={t("masterclass-description")}
            placeholder={t("type-description")}
            rows={4}
            {...form.getInputProps("description")}
          />

          <div>
            <Text size="sm" fw={500} mb="xs">{t("masterclass-banner")}</Text>
            <div {...getRootProps()} style={{ 
              border: '2px dashed var(--clr-gray-175)', 
              borderRadius: '8px', 
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: 'var(--clr-gray-100)'
            }}>
              <input {...getInputProps()} />
              {bannerFile ? (
                <Group justify="center">
                  <Avatar src={URL.createObjectURL(bannerFile)} size="xl" radius="md" />
                  <Text size="sm">{bannerFile.name}</Text>
                </Group>
              ) : masterclass?.banner ? (
                <Group justify="center">
                  <Avatar src={masterclass.banner} size="xl" radius="md" />
                  <Text size="sm" c="dimmed">{t("change-banner")}</Text>
                </Group>
              ) : (
                <Stack gap={5} align="center">
                  <IconPhoto size={30} color="var(--clr-gray-400)" />
                  <Text size="sm" c="dimmed">{t("drop-banner-here")}</Text>
                </Stack>
              )}
            </div>
          </div>

          <Group justify="flex-end" mt="xl">
            <Button variant="outline" onClick={close} disabled={isCreating || isUpdating}>
              {t("cancel")}
            </Button>
            <PermissionGate permission={masterclass ? "resources.edit" : "resources.create"}><Button type="submit" loading={isCreating || isUpdating}>
              {isEdit ? t("update") : t("submit")}
            </Button></PermissionGate>
          </Group>
        </Stack>
      </form>
        </Tabs.Panel>

        <Tabs.Panel value="form">
          <div style={{ padding: "20px 0" }}>
            {masterclass?._id && (
              <CustomFormBuilder serviceId={masterclass._id} />
            )}
          </div>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};
